import React, { createContext, useContext } from "react"
import { Card, CardProps } from "react-bootstrap"

import classNames from "classnames"

import { WithClassName } from "shared/types/WithClassName"
import { Skeleton } from "shared/ui/skeleton/Skeleton"

import "./styles/_portfolio-card.scss"

const PortfolioCardLoadingContext = createContext<boolean>(false)

const usePortfolioCardLoading = () => useContext(PortfolioCardLoadingContext)

interface PortfolioCardRootProps
  extends WithClassName,
    Omit<CardProps, "text"> {
  isLoading?: boolean
}

interface PortfolioCardIndicatorProps extends WithClassName {
  children?: React.ReactNode
}

interface PortfolioCardImageProps extends WithClassName {
  children?: React.ReactNode
}

interface PortfolioCardBodyProps extends WithClassName {
  children?: React.ReactNode
}

interface PortfolioCardTitleProps extends WithClassName {
  children?: React.ReactNode
}

interface PortfolioCardSubtitleProps extends WithClassName {
  children?: React.ReactNode
}

const PortfolioCardRoot = React.memo(
  ({
    isLoading = false,
    className,
    children,
    ...rest
  }: PortfolioCardRootProps) => (
    <PortfolioCardLoadingContext.Provider value={isLoading}>
      <Card
        className={classNames("portfolio-card", className)}
        data-testid="portfolio-card"
        {...rest}
      >
        {children}
      </Card>
    </PortfolioCardLoadingContext.Provider>
  )
)

const PortfolioCardIndicators = React.memo(
  ({ children, className }: PortfolioCardIndicatorProps) => {
    const isLoading = usePortfolioCardLoading()
    return (
      <div className={classNames("portfolio-card__indicators", className)}>
        {isLoading ? <Skeleton /> : children}
      </div>
    )
  }
)

const PortfolioCardImage = React.memo(
  ({ children, className }: PortfolioCardImageProps) => {
    const isLoading = usePortfolioCardLoading()

    return (
      <div
        className={classNames("portfolio-card__image card-image", className)}
      >
        {isLoading ? (
          <Skeleton fill containerClassName="image-loader" />
        ) : (
          children
        )}
      </div>
    )
  }
)

const PortfolioCardBody = React.memo(
  ({ children, className }: PortfolioCardBodyProps) => (
    <Card.Body className={classNames("portfolio-card__body", className)}>
      {children}
    </Card.Body>
  )
)

const PortfolioCardTitle = React.memo(
  ({ children, className }: PortfolioCardTitleProps) => {
    const isLoading = usePortfolioCardLoading()

    return (
      <Card.Title className={classNames("portfolio-card__title", className)}>
        {isLoading ? <Skeleton /> : children}
      </Card.Title>
    )
  }
)

const PortfolioCardSubtitle = React.memo(
  ({ children, className }: PortfolioCardSubtitleProps) => {
    const isLoading = usePortfolioCardLoading()

    return (
      <Card.Subtitle
        className={classNames("portfolio-card__subtitle", className)}
      >
        {isLoading ? <Skeleton /> : children}
      </Card.Subtitle>
    )
  }
)

export const PortfolioCard = Object.assign(PortfolioCardRoot, {
  Indicators: PortfolioCardIndicators,
  Image: PortfolioCardImage,
  Body: PortfolioCardBody,
  Title: PortfolioCardTitle,
  Subtitle: PortfolioCardSubtitle
})
